import React from 'react'
import styled from 'styled-components'

import { Ellipsis } from 'components/styled/typography'
import { SvgIcon, SvgIconType } from 'components/styled/icon/svg'
import { Icon } from '../../icon'
import {
  BodyStyle,
  LabelStyle,
  SmallStyle,
  SubtitleStyle,
} from '../../typography/styles'

const TextVariants = {
  LABEL: 'label',
  SMALL: 'small',
  BODY: 'body',
  SUBTITLE: 'subtitle',
} as const

export type TextVariant = typeof TextVariants[keyof typeof TextVariants]

export interface MenuButtonContentProps {
  icon?: string
  svgIcon?: SvgIconType
  iconRight?: string
  iconUseSymbols?: boolean
  useOutlinedIcon?: boolean
  text?: string | React.ReactNode // TODO: rename the text as the name does not really work if it is a node
  textVariant?: TextVariant
  isSubMenu: boolean
}

const MenuIcon = styled(Icon)<{ $text?: string | React.ReactNode }>`
  margin-right: ${({ theme, $text }) => ($text ? theme.space.s16 : 0)};
  flex-shrink: 0;
`

const SvgMenuIcon = styled(SvgIcon)`
  margin-right: ${({ theme }) => theme.space.s16};
  flex-shrink: 0;
`

const SubmenuIcon = styled(Icon)`
  margin-left: ${({ theme }) => theme.space.s16};
  flex-shrink: 0;
  color: inherit;
`

export const Name = styled.div<{
  $textVariant: TextVariant
  $isSubmenu: boolean
}>`
  flex-grow: 1;
  text-align: left;
  margin-right: ${({ theme, $isSubmenu }) =>
    $isSubmenu ? theme.space.s16 : 0};
  user-select: none;

  ${({ $textVariant }) => $textVariant === TextVariants.SMALL && SmallStyle};
  ${({ $textVariant }) => $textVariant === TextVariants.LABEL && LabelStyle};
  ${({ $textVariant }) => $textVariant === TextVariants.BODY && BodyStyle};
  ${({ $textVariant }) =>
    $textVariant === TextVariants.SUBTITLE && SubtitleStyle};
  ${Ellipsis};
  color: inherit;
`

// This is the button content component for all menu buttons, and links that share same styles.
// This component shouldn't be used for anything else.
// Use MenuButton, MenuLink, SubMenuButton or ExternalMenuLink components instead.

export const MenuButtonContent = ({
  icon,
  iconRight,
  svgIcon,
  text,
  isSubMenu,
  iconUseSymbols = false,
  useOutlinedIcon = false,
  textVariant = 'body',
}: MenuButtonContentProps) => {
  return (
    <>
      {icon && (
        <MenuIcon
          useOutlined={useOutlinedIcon}
          useSymbols={iconUseSymbols}
          $text={text}
          size="s20"
          aria-hidden
          type={icon}
        />
      )}
      {svgIcon && <SvgMenuIcon size="s20" aria-hidden type={svgIcon} />}
      <Name $textVariant={textVariant} $isSubmenu={isSubMenu}>
        {text}
      </Name>
      {iconRight && <Icon pl="s8" size="s20" aria-hidden type={iconRight} />}
      {isSubMenu && <SubmenuIcon size="s20" aria-hidden type="chevron_right" />}
    </>
  )
}
